import { isPS } from '@/utils';
import type { fundSheetTypeEnum, sheetTypeEnum } from '@/utils/enum';
import type z from 'zod';
import type { ProjectTableColumn } from '../projects/_models/project-filter';

type GetAvailableColumnsParams = {
  sheetType: z.infer<typeof sheetTypeEnum>;
  skipAdministrativeReformTeamEvaluation?: boolean | null;
  fundSheetType?: z.infer<typeof fundSheetTypeEnum> | null;
  isPublicSite?: boolean;
};

type ColumnVisibilityResult = {
  availableColumns: ProjectTableColumn[];
  initiallyVisibleColumns: ProjectTableColumn[];
  selectableColumns: ProjectTableColumn[];
  buildValidatedTableColumns: (columns: string[]) => string[];
};

// 固定列
const FIXED_COLUMNS: ProjectTableColumn[] = ['name'];

// 公開サイトで除外される列
const PUBLIC_SITE_EXCLUDED_COLUMNS: ProjectTableColumn[] = ['isPublished'];

// RS専用の公開サイト除外列
const RS_PUBLIC_SITE_EXCLUDED_COLUMNS: ProjectTableColumn[] = [
  'candidateReasonsOfInspectionByExternalExpert',
  'lastImplementedFiscalYear',
];

// SS専用の除外列
const SS_EXCLUDED_COLUMNS: ProjectTableColumn[] = [
  'inspectionByExternalExpertReviewType',
  'candidateReasonsOfInspectionByExternalExpert',
  'lastImplementedFiscalYear',
  'finalizedAmountForCarriedForward',
  'finalizedAmountForReserve',
  'finalizedAmount',
];

// RSシート列定義
const RS_INITIALLY_VISIBLE_COLUMNS: ProjectTableColumn[] = [
  'ministryName',
  'projectNumber',
  'startYear',
  'budgetData',
  'finalizedAmountForInitialBudget',
  'mainPayment',
  'paymentAmount',
];

const RS_AVAILABLE_COLUMNS: ProjectTableColumn[] = [
  'name',
  'isPublished',
  'inspectionByExternalExpertReviewType',
  'candidateReasonsOfInspectionByExternalExpert',
  'lastImplementedFiscalYear',
  'ministryName',
  'projectNumber',
  'startYear',
  'budgetData',
  'finalizedAmountForInitialBudget',
  'organizationLabel',
  'endYear',
  'purpose',
  'task',
  'previousYearExecutedAmounts',
  'previousYearExecutionRate',
  'nextYearRequestedAmount',
  'finalizedAmountForSupplementaryBudget',
  'finalizedAmountForCarriedForward',
  'finalizedAmountForReserve',
  'finalizedAmount',
  'mainPayment',
  'paymentAmount',
];

// KSシート列定義
const KS_COMMON_COLUMNS: ProjectTableColumn[] = [
  'ministryName',
  'fundSheetNumber',
  'fundSheetBranchNumber',
];

const KS_COLUMNS_CONFIG = {
  standard: {
    initiallyVisible: [
      ...KS_COMMON_COLUMNS,
      'beginningOfTheFundBalance',
      'ownershipRatio',
    ] as ProjectTableColumn[],
    available: [
      'name',
      'projectNumber',
      'isPublished',
      'ministryName',
      'fundOwner',
      'fiscalYearOfCreation',
      'endDateOfNewApplicationReceiving',
      'scheduledEndDate',
      'administrativeExpenses',
      'previousYearTotalIncome',
      'previousYearTotalExpense',
      'amountReturnedToTheNationalTreasury',
      'fundSheetNumber',
      'fundSheetBranchNumber',
      'operationForms',
      'businessForms',
      'beginningOfTheFundBalance',
      'ownershipRatio',
      'mainPayment',
      'paymentAmount',
      'previousYearAdministrativeExpenseRate',
      'previousYearDivergenceRate',
    ] as ProjectTableColumn[],
  },
  'local-government': {
    initiallyVisible: KS_COMMON_COLUMNS,
    available: [
      'name',
      'projectNumber',
      'isPublished',
      'ministryName',
      'fiscalYearOfCreation',
      'endDateOfNewApplicationReceiving',
      'scheduledEndDate',
      'fundSheetNumber',
      'fundSheetBranchNumber',
      'operationForms',
      'businessForms',
    ] as ProjectTableColumn[],
  },
  investment: {
    initiallyVisible: KS_COMMON_COLUMNS,
    available: [
      'name',
      'projectNumber',
      'isPublished',
      'ministryName',
      'fundSheetNumber',
      'fundSheetBranchNumber',
      'businessForms',
    ] as ProjectTableColumn[],
  },
} as const;

// KS列設定の取得
const getKSConfig = (fundSheetType: string | null) => {
  const type = fundSheetType as keyof typeof KS_COLUMNS_CONFIG;
  return KS_COLUMNS_CONFIG[type] || KS_COLUMNS_CONFIG.standard;
};

const SLICE_INDEX = 5;

// デフォルト列にisPublishedを挿入（内部サイトのみ）
const insertIsPublishedColumn = (
  columns: ProjectTableColumn[],
  sheetType: 'RS' | 'KS' | 'SS',
): ProjectTableColumn[] => {
  if (sheetType === 'KS') {
    return [
      ...columns,
      ...(isPS() ? [] : (['isPublished'] as ProjectTableColumn[])),
    ];
  }

  if (isPS() || columns.length < SLICE_INDEX) {
    return columns;
  }
  return [
    ...columns.slice(0, SLICE_INDEX),
    'isPublished',
    ...columns.slice(SLICE_INDEX),
  ];
};

// 公開サイト除外列の取得
const getPublicSiteExcludedColumns = (
  sheetType: 'RS' | 'KS' | 'SS',
): ProjectTableColumn[] => {
  if (sheetType === 'RS') {
    return [
      ...PUBLIC_SITE_EXCLUDED_COLUMNS,
      ...RS_PUBLIC_SITE_EXCLUDED_COLUMNS,
    ];
  }
  return PUBLIC_SITE_EXCLUDED_COLUMNS;
};

// メイン列取得ロジック
const getAvailableColumns = (
  params: GetAvailableColumnsParams,
): ProjectTableColumn[] => {
  const {
    sheetType,
    skipAdministrativeReformTeamEvaluation,
    fundSheetType,
    isPublicSite = isPS(),
  } = params;

  // KSシート
  if (sheetType === 'KS') {
    const config = getKSConfig(fundSheetType || null);
    const columns = config.available;

    return isPublicSite
      ? columns.filter(
          (col) => !getPublicSiteExcludedColumns('KS').includes(col),
        )
      : columns;
  }

  // SSシート
  if (sheetType === 'SS') {
    const baseColumns = RS_AVAILABLE_COLUMNS.filter(
      (col) => !SS_EXCLUDED_COLUMNS.includes(col),
    );

    return isPublicSite
      ? baseColumns.filter(
          (col) => !getPublicSiteExcludedColumns('SS').includes(col),
        )
      : baseColumns;
  }

  // RSシート
  const rsColumns =
    skipAdministrativeReformTeamEvaluation === true
      ? RS_AVAILABLE_COLUMNS.filter(
          (col) => col !== 'lastImplementedFiscalYear',
        )
      : RS_AVAILABLE_COLUMNS;

  return isPublicSite
    ? rsColumns.filter(
        (col) => !getPublicSiteExcludedColumns('RS').includes(col),
      )
    : rsColumns;
};

export const getColumnVisibility = (
  params: GetAvailableColumnsParams,
): ColumnVisibilityResult => {
  const availableColumns = getAvailableColumns(params);

  // 初期表示される列の取得
  const initialColumnKeys = (() => {
    if (params.sheetType === 'KS') {
      const config = getKSConfig(params.fundSheetType || null);
      return insertIsPublishedColumn(config.initiallyVisible, params.sheetType);
    }
    return insertIsPublishedColumn(
      RS_INITIALLY_VISIBLE_COLUMNS,
      params.sheetType,
    );
  })();

  // 利用可能な列でフィルタリング
  const initiallyVisibleColumns = initialColumnKeys.filter((key) =>
    availableColumns.includes(key),
  );

  // 選択可能な列（固定列を除外）
  const selectableColumns = availableColumns.filter(
    (key) => !FIXED_COLUMNS.includes(key),
  );

  // ローカルストレージから読み込んだ列を検証し、固定列と結合してテーブル用の列を構築
  const buildValidatedTableColumns = (columns: string[]): string[] => {
    // 有効な列のみをフィルタリング（固定列は除外）
    const validatedColumns = columns.filter((key) => {
      const column = key as ProjectTableColumn;
      return availableColumns.includes(column) && column !== 'name';
    });

    // 固定列と結合
    return [...FIXED_COLUMNS, ...validatedColumns];
  };

  return {
    availableColumns,
    initiallyVisibleColumns,
    selectableColumns,
    buildValidatedTableColumns,
  };
};
